import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircleIcon } from "@heroicons/react/24/solid";

function formatSentAt(value, locale) {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString(locale);
  } catch {
    return value;
  }
}

export default function SendLogPanel({ sendLog = [], lastSentAt, title, onExportSendLog }) {
  const { t, i18n } = useTranslation();
  const entries = Array.isArray(sendLog) ? sendLog : [];

  if (!lastSentAt && entries.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-5 text-sm text-slate-500">
        {t("documentationSend.not_sent_yet")}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {lastSentAt ? (
        <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1.5 text-sm font-semibold text-emerald-800 ring-1 ring-emerald-200">
          <CheckCircleIcon className="h-4 w-4" />
          {t("documentationSend.sent_badge", {
            date: formatSentAt(lastSentAt, i18n.language),
          })}
        </div>
      ) : null}

      <div>
        <div className="mb-2 flex items-center justify-between gap-3">
          <h4 className="text-sm font-semibold text-slate-900">
            {title || t("documentationSend.history_title")}
          </h4>
          {onExportSendLog && entries.length > 0 ? (
            <button
              type="button"
              onClick={onExportSendLog}
              className="text-xs font-semibold text-[#1463C7] hover:underline"
            >
              {t("documentationSend.export_send_log")}
            </button>
          ) : null}
        </div>
        <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white">
          {entries.map((entry) => (
            <li key={entry.id || `${entry.sent_at}-${entry.recipient_email}`} className="px-4 py-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900">
                    {entry.recipient_name || entry.recipient_email}
                  </p>
                  <p className="text-xs text-slate-500">{entry.recipient_email}</p>
                  {entry.message ? (
                    <p className="mt-2 text-xs text-slate-600">{entry.message}</p>
                  ) : null}
                  {entry.outbound_communication_id ? (
                    <Link
                      to={`/dashboard/communications/${entry.outbound_communication_id}`}
                      className="mt-2 inline-block text-xs font-semibold text-[#1463C7] hover:underline"
                    >
                      {t("documentationSend.view_email")}
                    </Link>
                  ) : null}
                </div>
                <div className="text-right text-xs text-slate-500">
                  <p>{formatSentAt(entry.sent_at, i18n.language)}</p>
                  {entry.sent_by_name ? (
                    <p className="mt-1">{entry.sent_by_name}</p>
                  ) : null}
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
